import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import {
  Card,
  CardContent,
  Avatar,
  Button,
  IconButton,
} from "@mui/material";
import { getLocalizedText } from "../../logic/Utils";
import { theme } from '../../../Template3/Template';
import { textVariant, textClasses } from '../../config';
import useServiceCard from '../../logic/useServiceCard';
import { useCompanyData } from '../../contexts/DataContext';
import NoImage from 'src/assets/images/no-pictures.png';
import { useTheme } from "@mui/styles";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import makeStyles from '@mui/styles/makeStyles';
import Carousel from 'react-elastic-carousel';
import SectionLayout from '../../layout/SectionLayout';
import {SectionTitle, SectionSubtitle} from '../../../Template3/components/SectionTitle';
import '../../styles.css'

const useStyles = makeStyles((theme) => ({
    carouselContainer: {
      position: "relative",
      width: "100%",
      "& .rec-carousel-item": {
        // ensure all cards have the same width/height
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      },
      "& .rec-carousel-wrapper": {
        padding: "1rem 0", // top/bottom spacing around the track
      },
      "& .rec-item-wrapper": {
        maxWidth: 'fit-content !important',
        borderRadius:'10px',
      },
      "& .rec-arrow": {
        // override default arrow style from react-elastic-carousel
        backgroundColor: "rgba(255,255,255,0.8)",
        "&:hover": {
          backgroundColor: "rgba(255,255,255,1)",
        },
        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
      },
      "& .rec-pagination": {
        display: "none", // hide dots / pagination
      },
    },
  
    cardRoot:props => ({
        minWidth: 'calc(208px - 30px)',
        maxWidth:'calc(208px - 30px)',
        width: '100%',
        maxHeight: 222,
        minHeight: 222,
        padding:'16px 15px',
        cursor:'pointer',
        borderRadius: 10,
        border:'1px solid #F2F2F2',
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        paddingTop: theme.spacing(2),
        backgroundColor: "#ffffff",
        boxShadow:props.shadow ? props?.shadow : 'none',
        "-webkit-box-shadow":props.shadow ? props?.shadow : 'none',
        "-moz-box-shadow":props.shadow ? props?.shadow : 'none',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease', // transition for smooth effect
        transform: 'scale(1)', // initial scale
        "&:hover": {
            boxShadow:'3px 6px 10px #0000001A',
            transform: 'scale(1.03)', // slightly enlarge on hover
        },
    }),
  
    avatarWrapper: {
        width: 111,
        height: 111,
        marginBottom: 20,
        borderRadius: "50%",
        overflow: "hidden",
    },

    avatarImage: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
    },
    titleText: {
        textAlign: "center",
        fontWeight: 500,
        fontSize: "18px",
        color: "#0F2240",
        textTransform:'capitalize',
        lineHeight: 1.2,
    },
    viewAllButton: {
        marginTop: theme.spacing(2),
        borderRadius: 10,
        padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
        backgroundColor: '#FFFFFF',
        color: "#0F2240",
        border:'1px solid #F2F2F2',
        textTransform: "none",
        fontWeight: 600,
        width:'calc(229px - 48px)',
        height:63,
        "&:hover": {
            backgroundColor: '#F4F6F6',
        },
    }
}));

export function Services({
    services,
    categories,
    t,
    handleExploreServices,
    handleExploreCategories,
    productType,
    data: dataObj,
    mainColor,
    contentData,
    index,
  }) {
    const classes = useStyles();
    const theme = useTheme();
    const isMounted = useRef(false);
    const { chatgptContent } = dataObj;
      const companyId = dataObj?.companyData ? dataObj?.companyData?.companyId : '';

    // Destructure all of your contentData props:
    const {
      maxDisplayItems,
      displayProductDesc,
      productDisplayHorizontal = true,
      backgroundColor,
      backgroundImage,
      backgroundStyle,
      categoryId,
      title,
      arTitle,
      subTitle,
      arSubTitle,
      selectedCategories,
      titleFormat,
      titleFontSize,
      subTitleFontSize,
      titleFontColor,
      subTitleFontColor,
      paddingTop,
      paddingBottom,
    } = contentData || {};

    // If displayProductDesc is undefined, default to true:
    const displayDescription =
      displayProductDesc !== undefined ? displayProductDesc : true;
  
    // Determine how many items to show at once:
    const maxServiceShow = maxDisplayItems ?? null;
    const [showItems, setShowItems] = useState(maxServiceShow);
  
    // Your mobile→desktop breakpoints for react-elastic-carousel:
    const breakPoints = [
      { width: 1, itemsToShow: 1 },
      { width: 600, itemsToShow: 2 },
      { width: 900, itemsToShow: 3 },
      { width: 1200, itemsToShow: 6 },
    ];
  
    // Build the localized titles:
    const isRTL = dataObj?.isRTL === "ar";
    const _title = title
     ? getLocalizedText(title, arTitle)
      : productType === 'categories'
       ? t?.("companyPage.exploreAllCategories")
       : t?.("companyPage.exploreAllServices");
   
      const _subTitle = subTitle
       ? getLocalizedText(subTitle, arSubTitle)
       : t?.("companyPage.coursesTitle2");
  
    // Filter services by categoryId if needed:
    const servicesDuplicate = services?.length
      ? JSON.parse(JSON.stringify(services))
      : [];
    const filterService =
      servicesDuplicate.length && categoryId
        ? servicesDuplicate.filter((v) => v.serviceCategoryId === categoryId)
        : servicesDuplicate;
  
    // Filter categories if user selected some:
    const filteredSelectedCategory =
      selectedCategories?.length && categories
        ? categories.filter((cat) => selectedCategories.includes(cat.id))
        : [];
  
    const filterCategories =
      filteredSelectedCategory.length > 0 ? filteredSelectedCategory : categories;
  
    // Decide exactly which list we will render:
    const renderList =
      productType === "categories"
        ? filteredSelectedCategory.length > 0
          ? filteredSelectedCategory
          : categories
        : filterService;

  const limitedList = maxServiceShow ? renderList.slice(0, maxServiceShow) : renderList;

  
    // effect to update showItems if maxDisplayItems changes:
    useEffect(() => {
      isMounted.current = true;
      setShowItems(maxServiceShow);
      return () => {
        isMounted.current = false;
      };
    }, [maxServiceShow]);
  
    // If there is nothing to show, return null:
    if (!limitedList || limitedList.length === 0) {
      return null;
    }
  
    return (
      <Box
        style={{
          width: "100%",
          // Set background per your contentData:
          ...(backgroundStyle === "image" && backgroundImage
            ? {
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
              }
            : backgroundStyle === "gradient"
            ? { backgroundImage: backgroundColor }
            : { backgroundColor: backgroundColor || "#FAFAFA" }),
        }}
      >
        {/* If you have a SectionLayout component, wrap as you originally had: */}
        <SectionLayout paddingTop={paddingTop} paddingBottom={paddingBottom}>
          <Box width="100%" m="auto" position="relative">
  
            {/* Main headings / subtitles */}
            <Box display="flex" flexDirection="column" alignItems="flex-start">
                <SectionTitle pFontSize={titleFontSize} color={titleFontColor} variant={titleFormat}>{_title}</SectionTitle>
                <SectionSubtitle visibleHorizontalLine={false} pFontSize={subTitleFontSize} color={subTitleFontColor} mainColor={mainColor}>{_subTitle}</SectionSubtitle>
            </Box>
            <Box pb={3} display="flex" flexDirection={!productDisplayHorizontal ? 'row' : 'unset'} flexWrap={!productDisplayHorizontal ? 'wrap' : 'unset'} justifyContent="center">
              <Carousel
              className={classes.carouselContainer}
                breakPoints={breakPoints}
                itemPadding={[5, 5]}
                pagination={false}
                renderArrow={(arrowProps) => {
                  const { type, onClick, isEdge } = arrowProps;
                  return type === "PREV" ? (
                    <IconButton
                      onClick={onClick}
                      disabled={isEdge}
                      style={{
                        position: "absolute",
                        left: -20,
                        top: "40%",
                        zIndex: 10,
                      }}
                    >
                      <ArrowBackIosIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={onClick}
                      disabled={isEdge}
                      style={{
                        position: "absolute",
                        right: -20,
                        top: "40%",
                        zIndex: 10,
                      }}
                    >
                      <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                  );
                }}
              >
                {limitedList.map((item, idx) => {
                  const displayName = chatgptContent && Object?.keys(chatgptContent)?.length ? (isRTL === 'ar' ? 
                    chatgptContent?.['arabic']?.[`section${index}`]?.[idx]?.['title'] : chatgptContent?.['english']?.[`section${index}`]?.[idx]?.['title'])
                    : item.name;
                  return (
                    <ServiceCard 
                        key={idx}
                        service={{
                            ...item,
                            name:displayName
                        }}
                        {...{displayDescription,productType,companyId,contentData}}
                    />
                  );
                })}
              </Carousel>
            </Box>
  
            <Box display="flex" justifyContent="center" mt={2}>
              <Button
                variant="contained"
                className={classes.viewAllButton}
                onClick={() => productType === "categories" ? handleExploreCategories() : handleExploreServices()}
              >
                {t && t("services.viewAll")}
              </Button>
            </Box>
          </Box>
        </SectionLayout>
      </Box>
    );
};

function ServiceCard({service,displayDescription,productType, companyId, contentData}) {
    const { data } = useCompanyData();
    const matches = useMediaQuery(theme.breakpoints.down('lg'));
    const { handleClick, rating, reviews } = useServiceCard({service,productType,companyId});
    const maxStringLength = matches ? 80 : 130;
    const classes = useStyles({shadow:contentData?.shadow,defaultShadow:'none'});

    const cardRadius = contentData?.cardRadius ? `${contentData?.cardRadius}px` : '0px'; 
    const cardBorder = contentData?.border || 'none'; 
  
    const titleFontSize = contentData?.cardTitleFontSize ? contentData?.cardTitleFontSize : '21';
  
    const titleFontColor = contentData?.cardTitleColor ? contentData?.cardTitleColor : '';
  
    return(
        <Card
        className={classes.cardRoot}
        onClick={handleClick}
        style={{
            cursor: 'pointer',
            ...((contentData?.cardBackgroundStyle === 'image' && contentData?.cardBackgroundImage) ? 
              {
                  backgroundSize: "cover",
                  backgroundImage: `url(${contentData?.cardBackgroundImage})`,
                  backgroundRepeat: "no-repeat",
              } 
            : 
            (contentData?.cardBackgroundStyle === 'gradient' && contentData?.cardBackgroundColor) ? 
              {
                  backgroundImage: contentData?.cardBackgroundColor ? contentData?.cardBackgroundColor : '#ffffff',
              } 
            :
            (contentData?.cardBackgroundStyle === 'solidColor' && contentData?.cardBackgroundColor) ? 
              {
                  backgroundColor: contentData?.cardBackgroundColor || '#ffffff',
              } 
            : 
              {backgroundColor: '#fff'}
            ),
            overflow:'hidden'
          }}
          border={cardBorder}
          borderRadius={cardRadius}
      >
          <Box className={classes.avatarWrapper}>
            <Avatar
              alt={service?.name || 'service'}
              src={productType === 'categories' ? service.photo ? service.photo : NoImage : service?.cover}
              className={classes.avatarImage}
            />
          </Box>

          <CardContent style={{ flexGrow: 1, padding: 0,     display:"flex",
    alignItems:"center",
    justifyContent:"center",
    height:"85px",
    width:"100%" }}>
            <Typography
              variant={textVariant?.[titleFontSize]}
              className={`twoLineTxt ${classes.titleText} ${textClasses?.[titleFontSize]}`}
              style={{...(titleFontColor ? {color:titleFontColor} : {})}}
            >
              {service?.name}
            </Typography>
          </CardContent>
      </Card>
    )
}